export const enDataHub = {
  breadcrumb: {
    appStudio: "App Studio",
    dataHub: "Data Hub",
    aria: "Breadcrumb",
  },
  title: "Data Hub",
  subtitle: "Connect and manage your data sources",
  subtitleDomains: "Organize datasets into domains for AI context",
  tabs: {
    connections: "Connections",
    domains: "Domains",
    aria: "Data hub sections",
  },
  actions: {
    newConnection: "New Connection",
    newDomain: "New Domain",
  },
  domains: {
    unknownConnection: "Unknown connection",
    menuAria: "Actions for {{name}}",
    connectionLabel: "Connection: {{name}}",
    exportAction: "Export",
    exportFailed: "Could not export the domain. Please try again.",
  },
  domainDetail: {
    tablesLibrary: {
      title: "Tables Library",
      viewOnlyNote: "Browse the tables in this domain.",
      subtitle: "Select and move the tables to canvas",
      columnCount: "{{count}} columns",
      tableCount: "{{count}} tables",
      columnCheckboxAria: "Include {{column}} from {{table}}",
      searchPlaceholder: "Search schemas and tables",
      searchAria: "Search schemas and tables",
      noSearchResults: "No schemas or tables match your search.",
    },
    backAria: "Back to Data Hub",
    loadFailed: "Could not load domain tables.",
    retry: "Retry",
    notFound: "Domain not found",
    mode: {
      aria: "Domain entity mode",
      draft: "Draft",
      published: "Published",
    },
    publish: {
      action: "Publish",
      success: "Published successfully",
      failed: "Semantic model generation failed. Reverted to draft.",
    },
    discardDraft: {
      action: "Discard draft",
    },
    overview: {
      tab: "Overview",
      loading: "Loading business context",
      fields: {
        domainPurpose: "Domain purpose",
        stakeholders: "Stakeholders",
        priorityMetrics: "Priority metrics",
      },
      placeholders: {
        domainPurpose: "What this dataset is for and who uses it",
        stakeholders: "Type a stakeholder and press Enter",
        priorityMetrics: "Type a metric and press Enter",
      },
      actions: {
        save: "Save",
        saveSucceeded: "Business context saved.",
      },
      errors: {
        loadFailed: "Unable to load business context.",
        saveFailed: "Unable to save business context.",
      },
    },
    nuggetConfig: {
      tab: "Nugget Configs",
      title: "Nugget configs",
      subtitle: "What the nugget pipeline looks at, over which window, and how often it runs.",
      newTitle: "New nugget config",
      editTitle: "Edit nugget config",
      loading: "Loading nugget configs",
      empty: "No nugget configs yet.",
      emptySchedules: "No frequency set — this config will not run on a schedule.",
      menuAria: "Actions for {{name}}",
      fields: {
        name: "Name",
        description: "Description",
        focusAreas: "Focus areas",
        timeframe: "Timeframe",
        timeframeMode: "Timeframe mode",
        amount: "Last",
        unit: "Unit",
        startDate: "From",
        endDate: "To",
        schedules: "Frequency",
        kind: "Repeats",
        dayOfWeek: "On",
        dayOfMonth: "Day",
        monthDayMode: "Which day",
        runAtTime: "At (UTC)",
        semanticMateriality: "Judge materiality from the semantic model",
        candidateSeeding: "Scan targets",
        interval: {
          daily: "Every N days",
          weekly: "Every N weeks",
          monthly: "Every N months",
          quarterly: "Every N quarters",
        },
      },
      placeholders: {
        name: "e.g. Weekly exec digest",
        description: "What this config is for",
        focusAreas: "Type a focus area and press Enter",
      },
      help: {
        focusAreas: "Areas the pipeline should prioritise, e.g. revenue, churn.",
        timeframe: "Which window of domain data each run should read.",
        localTime: "{{time}} in your local time ({{zone}})",
        semanticMateriality:
          "Uses each metric's declared unit and its own scale to decide what counts as a " +
          "material move. Turn off to compare against the previous name-based thresholds.",
        candidateSeeding: {
          off:
            "Every metric is scanned against every dimension it can be broken down by. " +
            "Approved candidates in the semantic model do not change what gets looked at.",
          augment:
            "Adds the metrics and breakdowns your approved candidates ask for on top of the " +
            "usual scan. Nothing is dropped, so runs take longer but cover more.",
          only:
            "Scans only what your approved candidates ask for. Much narrower and faster, and " +
            "anything they do not mention is not looked at this run.",
        },
      },
      candidateSeedingModes: {
        off: "Everything (default)",
        augment: "Everything, plus approved candidates",
        only: "Approved candidates only",
      },
      timeframeModes: {
        rolling: "Rolling",
        calendar: "Calendar",
        absolute: "Absolute",
      },
      timeframePresets: {
        last_1_day: "Last 1 day",
        last_7_days: "Last 7 days",
        last_30_days: "Last 30 days",
        last_calendar_week: "Last calendar week (Mon–Sun)",
        last_calendar_month: "Last calendar month",
        last_calendar_quarter: "Last calendar quarter",
        last_calendar_year: "Last calendar year",
        custom: "Custom…",
      },
      units: {
        hour: "Hours",
        day: "Days",
        week: "Weeks",
        month: "Months",
        quarter: "Quarters",
        year: "Years",
      },
      unitsPlural: {
        hour_one: "hour",
        hour_other: "hours",
        day_one: "day",
        day_other: "days",
        week_one: "week",
        week_other: "weeks",
        month_one: "month",
        month_other: "months",
        quarter_one: "quarter",
        quarter_other: "quarters",
        year_one: "year",
        year_other: "years",
      },
      scheduleKinds: {
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
        quarterly: "Quarterly",
      },
      schedulePresets: {
        every_day: "Every day",
        every_week: "Every Monday",
        every_2_weeks: "Every 2 weeks on Monday",
        first_of_month: "First day of every month",
        last_of_month: "Last day of every month",
        first_of_quarter: "First day of every quarter",
        last_of_quarter: "Last day of every quarter",
      },
      monthDayModes: {
        day: "On a set day",
        last: "Last day",
      },
      weekdays: {
        monday: "Monday",
        tuesday: "Tuesday",
        wednesday: "Wednesday",
        thursday: "Thursday",
        friday: "Friday",
        saturday: "Saturday",
        sunday: "Sunday",
      },
      summary: {
        rolling: "Last {{amount}} {{unit}}",
        calendar: "Last {{amount}} calendar {{unit}}",
        absolute: "{{start}} to {{end}}",
        schedule: {
          daily_one: "Every day",
          daily_other: "Every {{count}} days",
          weekly_one: "Every {{weekday}}",
          weekly_other: "Every {{count}} weeks on {{weekday}}",
          monthlyDay_one: "Day {{day}} of every month",
          monthlyDay_other: "Day {{day}} of every {{count}} months",
          monthlyLast_one: "Last day of every month",
          monthlyLast_other: "Last day of every {{count}} months",
          quarterlyDay_one: "Day {{day}} of every quarter",
          quarterlyDay_other: "Day {{day}} of every {{count}} quarters",
          quarterlyLast_one: "Last day of every quarter",
          quarterlyLast_other: "Last day of every {{count}} quarters",
          atTime: "{{when}} at {{time}} UTC",
        },
      },
      actions: {
        newConfig: "New config",
        addSchedule: "Add frequency",
        removeSchedule: "Remove frequency",
        create: "Create config",
        save: "Save config",
        edit: "Edit config",
        delete: "Delete config",
        cancel: "Cancel",
      },
      run: {
        start: "Run now",
        starting: "Starting…",
        streaming: "Run started. Streaming live progress…",
        hint: "Run this config, or pick a previous run to inspect its steps.",
        complete:
          "Run complete — {{signals}} signals, {{stories}} story candidates, " +
          "{{nuggets}} nuggets published.",
        completeCatalog:
          "{{count}} nugget(s) saved to the catalog. Open the Nuggets page to review them.",
        failed: "Run failed: {{message}}",
      },
      errors: {
        loadFailed: "Unable to load nugget configs.",
        nameRequired: "Name is required.",
        duplicateSchedule: "Two frequencies are identical — remove or change one before saving.",
        absoluteRecurring:
          "An absolute date range means every scheduled run reads the same fixed window.",
      },
    },
    documents: {
      tab: "Documents",
      description:
        "PDF documents for this domain. Up to {{limit}} files per upload, {{maxMb}} MB each.",
      addFiles: "Add PDF file(s)",
      uploading: "Uploading…",
      uploadSucceeded: "Documents uploaded.",
      empty: "No documents have been added to this domain yet.",
      table: {
        fileName: "File name",
        embeddingStatus: "Sync",
        addedAt: "Added",
        lastModifiedAt: "Last modified",
        actions: "Actions",
        ariaLabel: "Domain documents",
      },
      embedding: {
        action: "Sync Documents",
        running: "Syncing…",
        started: "Sync started. This can take a few minutes.",
        completed: "Document sync finished.",
        completedWithFailures: "Document sync finished — some files failed.",
        notEmbedded: "Not synced",
      },
      deleteAria: "Delete {{filename}}",
      deleteDialog: {
        heading: "Delete document?",
        subheading:
          "{{filename}} will be permanently removed from storage. This cannot be undone.",
        cancel: "Cancel",
        confirm: "Delete Document",
      },
      errors: {
        someFilesFailed: "These files were not uploaded:",
        fileLine: "{{filename}} — {{message}}",
        notPdf: "Only PDF files are supported.",
        tooLarge: "The file is larger than {{maxMb}} MB.",
        tooManyFiles:
          "You selected {{count}} files. Add at most {{limit}} files at a time.",
      },
    },
    access: {
      tab: "Access",
      empty: "No one has been granted a role on this domain yet.",
      addAccess: "Add Access",
      table: {
        user: "User",
        role: "Role",
        createdAt: "Granted",
        actions: "Actions",
        ariaLabel: "Domain access grants",
      },
      deleteAria: "Remove {{role}} access for {{user}}",
      deleteDialog: {
        heading: "Remove access?",
        subheading:
          "{{user}} will lose the {{role}} role on this domain. You can grant it again later.",
        cancel: "Cancel",
        confirm: "Remove Access",
      },
      errors: {
        deleteFailed: "Failed to remove access. Please try again.",
      },
      newAccessModal: {
        title: "Add Access",
        closeAria: "Close dialog",
        fields: {
          user: "User",
          role: "Role",
        },
        validation: {
          required: "This field is required.",
        },
        actions: {
          cancel: "Cancel",
          addAccess: "Add Access",
        },
        errors: {
          saveFailed: "Failed to add access. Please try again.",
        },
      },
    },
    canvas: {
      tab: "Canvas",
      tabsAria: "Domain workspace sections",
      empty: "Select and add the tables from the library to build your canvas",
      addSelected: "ADD TO CANVAS",
      updateSelected: "UPDATE THE CANVAS",
      saveSuccess: "Updated successfully",
      deleteSuccess: "Deleted successfully",
      saving: "Saving…",
      dropAria: "Canvas drop area",
      deleteTableAria: "Remove {{table}} from canvas",
      deleteColumnAria: "Remove {{column}} from {{table}}",
      pk: "PK",
      fk: "FK",
    },
    semanticModel: {
      tab: "Enrichment",
      empty: "Publish your domain to generate a semantic model.",
      pending:
        "Semantic model generation is queued. This page will update automatically.",
      running: "Generating semantic model…",
      progress: {
        hint: "This page updates automatically while enrichment runs.",
        percent: "{{value}}% complete",
        stages: {
          queued: "Queued for enrichment",
          fetching_metadata: "Fetching warehouse metadata",
          sampling_tables: "Sampling table rows",
          discovering_semantics: "Discovering semantic meaning",
          persisting_and_embedding: "Saving model and building search indexes",
        },
      },
      failedTitle: "Generation failed",
      failedHintReverted:
        "The domain has been moved back to draft so you can adjust the workspace and publish again.",
      failedHintPublished:
        "Your published tables are unchanged. Retry enrichment after fixing any issues below.",
      retryEnrichment: "Retry enrichment",
      retryPublish: "Publish again",
      skippedTitle: "Generation not run",
      readiness: {
        title: "Domain AI Readiness",
        overallScore: "Overall score: {{score}}%",
        statusEnriched: "Enriched",
        statusNotEnriched: "Not enriched",
        lastEnriched: "Last enriched: {{date}}",
        lastEnrichedNever: "Last enriched: never",
        entitiesDefined: "Entities Defined",
        relationshipsOriented: "Relationships Oriented",
        needsInput: "Needs Input",
        pendingProposals: "Pending Proposals",
        readyChip: "{{score}}% Ready",
        rerun: "Re-run AI Enrichment",
        rerunWithAnswers: "Save Answers & Re-run Enrichment",
      },
      graph: {
        viewButton: "View model graph",
        overlayTitle: "Semantic model graph",
        overlayClose: "Close graph",
        empty: "No entities to display in the graph yet.",
        legendFactEntity: "Fact entity",
        legendDimensionEntity: "Dimension entity",
        legendRelationship: "Relationship",
        legendRule: "Business rule",
        legendMeasure: "Measure",
        legendOntology: "Ontology",
        legendGlossary: "Glossary term",
        legendDimension: "Dimension",
        legendMetric: "Metric",
        canvasHint:
          "Force-directed layout. Pan and zoom to explore. Click a node to view its details.",
        detailsHint: "Click a node on the graph to view its metadata, fields, and assumptions.",
        detailsDescription: "Description",
        detailsMetadata: "Metadata",
        detailsRelationships: "Relationships",
        nodeKindEntity: "Entity",
        needsClarification: "Needs clarification",
      },
      rerun: {
        queued: "Enrichment re-run queued. Updates will appear automatically.",
        failed: "Could not re-run enrichment. Please try again.",
      },
      syncIndexes: {
        action: "Update search & graph",
        hint:
          "After manual edits, rebuild embeddings and the relationship graph from the saved model. This does not re-run AI enrichment.",
        succeeded: "Search and graph indexes updated.",
        failed: "Could not update search indexes. Please try again.",
      },
      sections: {
        overview: "Overview",
        entityDefinitions: "Entity Definitions",
        relationshipSemantics: "Relationship Semantics",
        businessGlossary: "Business Glossary",
        queryPatterns: "Query Patterns",
        measureDefinitions: "Measures",
        metricDefinitions: "Metrics",
        ontologies: "Ontologies",
        dimensionDefinitions: "Dimension Definitions",
        rules: "Business rules",
        candidates: "Candidates",
        proposals: "Pending Proposals",
        synonyms: "Synonyms",
        clarifications: "Clarifications",
        clarificationsOpen: "Open Clarifications",
        clarificationsAnswered: "Previously Answered",
        rationale: "Generation rationale",
        assumptions: "Assumptions",
        answerInChat: "Answer in chat",
        answerInChatCurrent: "Current question in chat",
        assumptionNoQuestion:
          "No interview question yet — add context in clarification chat.",
        validationErrors: "Validation issues",
      },
      queryPatterns: {
        askInChat: "Ask in chat: {{question}}",
      },
      clarifications: {
        title: "Clarifications",
        subtitle:
          "Answer open questions and re-run enrichment to refine the semantic model. Previously answered questions can be edited.",
        openHeading: "Open Clarifications",
        openHeadingCount: "{{count}} unanswered",
        answeredHeading: "Previously Answered",
        answeredHeadingCount: "{{count}} answered",
        noOpen: "No open clarifications. All previously generated questions have been answered.",
        noClarifications:
          "No clarification questions were raised by the AI. You can still re-run enrichment to refresh the model.",
        answerPlaceholder: "Type your answer here…",
        answerLabel: "Your answer",
        rationaleLabel: "Why we are asking",
        unsavedNote: "You have unsaved answers.",
      },
      chat: {
        title: "Semantic Model Chat",
        subtitle:
          "Ask questions about the current model or share corrections and context. Structured questions appear when needed — use Apply & Re-generate to refresh the model.",
        emptyTranscript:
          "No conversation yet. Ask about the model or share context to get started.",
        autoResolvedNote:
          "Auto-resolved by an earlier answer: {{reason}}",
        autoResolvedChip: "Auto-resolved",
        skipped: "Skipped by user",
        inputPlaceholder: "Ask about the model or share context for regeneration…",
        send: "Send",
        sending: "Sending…",
        skipQuestion: "Skip this question",
        applyRefine: "Apply & Re-generate model",
        applyRefineDisabled: "Apply & Re-generate (publish the domain first)",
        openCount: "{{count}} open",
        openCountSingle: "1 open",
        needsInputCount: "{{count}} need input",
        needsInputCountSingle: "1 needs input",
        noOpenQuestionsNeedsInput:
          "No structured questions are queued, but {{count}} model items still need input. Ask in chat or use Apply & Re-generate.",
        resolvedCount: "{{count}} resolved",
        progressLabel: "{{resolved}} of {{total}} resolved",
        remainingItemsTitle: "Still needs input",
        remainingItemsCountPlural: "({{count}} items)",
        remainingItemsCountSingular: "(1 item)",
        askAboutItem: "Ask me →",
        reviewSkippedButton: "Review skipped",
        reviewSkippedHint_one: "1 question was skipped — tap to re-ask it.",
        reviewSkippedHint_other: "{{count}} questions were skipped — tap to re-ask them.",
        focusedTargetChip: "Describing: {{label}}",
        clearFocusedTarget: "Clear",
        notReadyTitle: "Chat not available yet",
        notReadyHint:
          "Publish the domain and let the first enrichment finish before starting a clarification chat.",
        sendFailed: "Something went wrong. Please try again.",
        openFabAria: "Open semantic model chat",
        closeFabAria: "Close semantic model chat",
      },
      counts: {
        enriched: "{{ready}} of {{total}} enriched",
        zeroEnriched: "0 enriched",
      },
      empty_section: {
        entities:
          "No entity definitions yet. Run AI enrichment to generate them, or add one manually.",
        relationships:
          "No relationships were detected between the published tables. Add one manually to define how entities connect.",
        glossary: "No business glossary terms yet. Run AI enrichment to generate them, or add one manually.",
        queryPatterns: "No example query patterns were generated.",
        measures:
          "No measures yet. Run AI enrichment to generate them, or add one manually.",
        metrics:
          "No metrics defined yet. A metric combines one or more measures — possibly across tables — into a named analytical concept, e.g. Return Rate = Returns Amount ÷ Orders Amount.",
        ontologies:
          "No ontologies yet. Run AI enrichment to discover classification hierarchies, or add one manually.",
        dimensions:
          "No dimension definitions yet. Run AI enrichment to generate them, or add one manually.",
        rules:
          "No business rules yet. Run AI enrichment to generate them, or add one manually.",
        candidates:
          "No candidates yet. Candidate generation runs automatically after enrichment, or add one manually.",
        proposals: "No pending proposals. Regeneration hasn't suggested any changes to review yet.",
        semanticMeaning: "No semantic meaning defined",
      },
      labels: {
        grain: "Grain",
        primaryTable: "Primary table",
        dimensions: "Dimensions",
        primaryKeys: "Primary keys",
        backingSources: "Backing sources",
        expression: "Expression",
        aggregation: "Aggregation",
        sourceTable: "Source table",
        joinCondition: "Condition",
        joinType: "Type",
        physicalJoin: "Join",
        cardinalityFallback: "relation",
        confidence: "Confidence",
        provenance: "Source",
        sourceBinding: "Source binding",
        fieldBinding: "{{table}}.{{column}}",
        fieldBindingTableOnly: "{{table}}",
      },
      entities: {
        addAction: "Add entity",
        addFirst: "Add your first entity",
        addTitle: "Add entity",
        editTitle: "Edit entity",
        detailTitle: "Entity details",
        fieldsSection: "Fields",
        openDetailAria: "View details for {{name}}",
        editAria: "Edit {{name}}",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save entity",
        saveFailed: "Could not save entity changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete entity",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteConfirmationReferenced:
          'Are you sure you want to delete "{{name}}"? It is referenced by {{count}} other item(s) (relationships, rules, dimensions, or measures) — deleting it may leave those references broken. This action cannot be undone.',
        deleteFailed: "Could not delete entity. Please try again.",
        deleteSuccess: "Entity deleted successfully.",
        nameRequired: "Entity name is required.",
        duplicateKey: "An entity with this key already exists.",
        keyLabel: "Key",
        fields: {
          name: "Name",
          description: "Description",
          entityType: "Entity type",
        },
        entityFields: {
          name: "Field name",
          description: "Field description",
          dataType: "Data type",
          sourceColumn: "Source column",
          sourceColumnPlaceholder: "Select a column",
          sourceColumnEmpty: "Add tables to the canvas to choose columns.",
          sourceColumnInvalid: "This column is not available in the domain.",
          toggleSchemaAria: "Toggle schema {{schema}}",
          toggleTableAria: "Toggle table {{table}}",
        },
      },
      relationships: {
        addAction: "Add relationship",
        addFirst: "Add your first relationship",
        addTitle: "Add relationship",
        editTitle: "Edit relationship",
        viewTitle: "Relationship details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save relationship",
        saveFailed: "Could not save relationship changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete relationship",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete relationship. Please try again.",
        deleteSuccess: "Relationship deleted successfully.",
        nameRequired: "Relationship name is required.",
        entitiesRequired: "Select both source and target entities.",
        sameEntityError: "Source and target entities must be different.",
        duplicateKey: "A relationship with this key already exists.",
        keyLabel: "Key",
        fields: {
          name: "Name",
          sourceEntity: "From entity",
          targetEntity: "To entity",
          relationshipType: "Relationship type",
          cardinality: "Cardinality",
          joinFrom: "Join from",
          joinTo: "Join to",
          joinColumnPlaceholder: "Select a column",
          joinColumnEmpty: "Add tables to the canvas to choose columns.",
          joinColumnInvalid: "This column is not available in the domain.",
        },
      },
      glossary: {
        addAction: "Add term",
        addFirst: "Add your first glossary term",
        addTitle: "Add glossary term",
        editTitle: "Edit glossary term",
        viewTitle: "Glossary term details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save term",
        saveFailed: "Could not save glossary changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete glossary term",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete glossary term. Please try again.",
        nameRequired: "Term name is required.",
        duplicateKey: "A glossary term with this key already exists.",
        keyLabel: "Key",
        synonymsLabel: "Also known as",
        relatedMeasuresLabel: "Related metrics",
        fields: {
          name: "Term",
          definition: "Definition",
          synonyms: "Synonyms",
          synonymsPlaceholder: "e.g. ARR, annual recurring revenue",
          relatedMeasures: "Related metrics",
          relatedMeasuresPlaceholder: "e.g. revenue, mrr",
        },
      },
      ontologies: {
        addAction: "Add ontology",
        addFirst: "Add your first ontology",
        addTitle: "Add ontology",
        editTitle: "Edit ontology",
        viewTitle: "Ontology details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save ontology",
        saveFailed: "Could not save ontology changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete ontology",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete ontology. Please try again.",
        deleteSuccess: "Ontology deleted successfully.",
        nameRequired: "Ontology name is required.",
        duplicateKey: "An ontology with this key already exists.",
        keyLabel: "Key",
        orderedBadge: "Ordered",
        aiReasoningBadge: "AI Reasoning",
        nodeCount: "{{count}} nodes",
        nodeCount_one: "1 node",
        fields: {
          name: "Name",
          description: "Description",
          category: "Category",
          categoryNone: "None",
          sourceField: "Source field",
          nodes: "Nodes",
          nodesPlaceholder: "One node per line, e.g.\nBronze\nSilver\nGold",
          isOrdered: "Ordered hierarchy",
          aiReasoning: "AI reasoning",
        },
      },
      measurement: {
        sectionTitle: "Measurement",
        unitType: "Unit type",
        unitTypeHelp:
          "How this value is measured. Pick one or type your own — the vocabulary is open.",
        unitSymbol: "Unit symbol",
        decimalPlaces: "Decimal places",
        multiplier: "Display multiplier",
        materialityBasis: "Material when",
        basisOptions: {
          auto: "Not set — decide automatically",
          absolute: "It moves by an absolute amount",
          percent: "It moves by a percentage",
          share: "It is a large enough share of the total",
          range: "It leaves a normal range",
        },
        minAbsDelta: "Minimum change",
        minLevel: "Minimum level",
        // Percentage points, not a fraction -- the gate compares this against a
        // percent_delta already multiplied by 100, unlike the share field below.
        minPercentChange: "Minimum change in percent (10 = 10%)",
        minShareOfTotal: "Minimum share of total (0.1 = 10%)",
        normalLow: "Normal range low",
        normalHigh: "Normal range high",
        rangeInverted: "The low bound must not be above the high bound.",
        notes: "Notes",
        autoSummary:
          "Not set — materiality is judged relative to this metric's own scale.",
        summaryLabel: "Materiality",
      },
      measures: {
        addAction: "Add measure",
        addFirst: "Add your first measure",
        addTitle: "Add measure",
        editTitle: "Edit measure",
        viewTitle: "Measure details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save measure",
        saveFailed: "Could not save measure changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete measure",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteConfirmationReferenced:
          'Are you sure you want to delete "{{name}}"? It is referenced by {{count}} metric(s) — deleting it may leave those metrics broken. This action cannot be undone.',
        deleteFailed: "Could not delete measure. Please try again.",
        deleteSuccess: "Measure deleted successfully.",
        nameRequired: "Measure name is required.",
        duplicateKey: "A measure with this key already exists.",
        keyLabel: "Key",
        noEntity: "None",
        fields: {
          name: "Name",
          description: "Description",
          metricType: "Metric type",
          entity: "Entity",
        },
      },
      metrics: {
        addAction: "Add metric",
        addFirst: "Add your first metric",
        addTitle: "Add metric",
        editTitle: "Edit metric",
        viewTitle: "Metric details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save metric",
        saveFailed: "Could not save metric changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete metric",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete metric. Please try again.",
        deleteSuccess: "Metric deleted successfully.",
        nameRequired: "Metric name is required.",
        duplicateKey: "A metric with this key already exists.",
        keyLabel: "Key",
        componentsRequired: "Select at least one component measure.",
        executable: "Executable",
        notExecutable: "Not executable",
        notExecutableReason: "Not executable: {{reason}}",
        fields: {
          name: "Name",
          description: "Description",
          combinationStrategy: "Combination",
          componentMeasures: "Component measures",
        },
        combinationStrategyOptions: {
          passthrough: "Passthrough (single measure)",
          ratio: "Ratio (A ÷ B)",
          difference: "Difference (A − B)",
          sum: "Sum (A + B + …)",
          custom: "Custom",
        },
      },
      dimensions: {
        addAction: "Add dimension",
        addTitle: "Add dimension",
        editTitle: "Edit dimension",
        viewTitle: "Dimension details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        saveFailed: "Could not save dimension changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete dimension",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteSuccess: "Dimension deleted successfully.",
        deleteFailed: "Could not delete dimension. Please try again.",
        fields: {
          name: "Name",
          description: "Description",
        },
      },
      rules: {
        addAction: "Add rule",
        addFirst: "Add your first business rule",
        addTitle: "Add business rule",
        editTitle: "Edit business rule",
        viewTitle: "Business rule details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save rule",
        saveFailed: "Could not save business rule changes. Please try again.",
        deleteAction: "Delete",
        deleteAria: "Delete {{name}}",
        deleteTitle: "Delete business rule",
        deleteConfirmation:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
        deleteFailed: "Could not delete business rule. Please try again.",
        nameRequired: "Rule name is required.",
        duplicateKey: "A rule with this key already exists.",
        keyLabel: "Key",
        noEntity: "None",
        mappingHint: "One mapping per line, e.g. A=active",
        filterExpressionHint:
          "Optional SQL predicate applied to queries. Leave blank to derive from code mappings on save.",
        fields: {
          name: "Name",
          description: "Description",
          field: "Field",
          mapping: "Code mappings",
          filterExpression: "Filter expression",
          entity: "Entity",
        },
      },
      proposals: {
        acceptAction: "Accept",
        rejectAction: "Dismiss",
        acceptAria: "Accept proposal for {{name}}",
        rejectAria: "Dismiss proposal for {{name}}",
        acceptSucceeded: "Proposal accepted and applied to the model.",
        acceptFailed: "Could not accept the proposal. Please try again.",
        rejectSucceeded: "Proposal dismissed.",
        rejectFailed: "Could not dismiss the proposal. Please try again.",
        targetTypes: {
          entity: "Entity",
          relationship: "Relationship",
          rule: "Rule",
          measure: "Measure",
          dimension: "Dimension",
          metric: "Metric",
          ontology: "Ontology",
          synonym: "Synonym",
          glossary: "Glossary",
          measure_format: "Measure format",
          dimension_format: "Dimension format",
          metric_format: "Metric format",
          measure_materiality: "Measure materiality",
          metric_materiality: "Metric materiality",
          candidate: "Candidate",
        },
      },
      review: {
        approve: "Approve",
        reject: "Reject",
        approveAria: "Approve {{name}}",
        rejectAria: "Reject {{name}}",
        approveAll: "Approve all",
        rejectAll: "Reject all",
        approveModel: "Approve entire model",
        rejectModel: "Reject entire model",
        approvedBadge: "Approved",
        unreviewedBadge: "Needs review",
        approveFailed: "Could not approve this item. Please try again.",
        rejectFailed: "Could not reject this item. Please try again.",
        approveSectionFailed: "Could not approve this section. Please try again.",
        rejectSectionFailed: "Could not reject this section. Please try again.",
        conflictTitle: "Regeneration proposed a change to this approved item",
        conflictCurrent: "Current (approved)",
        conflictProposed: "Proposed",
        keepCurrent: "Keep current",
        useProposed: "Use proposed",
        rejectConfirmTitle: "Reject {{name}}?",
        rejectConfirmBody:
          "There is no approved value to fall back to, so this will remove it from the semantic model.",
        rejectConfirmConfirm: "Reject and remove",
        rejectConfirmCancel: "Cancel",
      },
      candidates: {
        addAction: "Add candidate",
        addFirst: "Add your first candidate",
        addTitle: "Add candidate",
        editTitle: "Edit candidate",
        viewTitle: "Candidate details",
        editAria: "Edit {{name}}",
        viewAria: "View {{name}} details",
        deleteAria: "Delete {{name}}",
        closeAria: "Close dialog",
        cancel: "Cancel",
        save: "Save",
        saveFailed: "Could not save candidate changes. Please try again.",
        metricsRequired: "Select at least one metric.",
        businessQuestionRequired: "Business question is required.",
        relationshipTypeNone: "None",
        missingBusinessContext:
          "Add your company and dataset context to get more relevant, better-prioritized candidates.",
        missingBusinessContextAction: "Add business context",
        fields: {
          metrics: "Metrics",
          relationshipType: "Relationship type",
          businessQuestion: "Business question",
          dimensions: "Dimensions",
          priority: "Priority",
          reasonForRunning: "Reason for running",
        },
        relationshipTypeOptions: {
          ratio: "Ratio",
          correlation: "Correlation",
          delta: "Delta",
          trend: "Trend",
          cluster: "Cluster",
        },
        priorityOptions: {
          high: "High",
          medium: "Medium",
          low: "Low",
        },
        priorityChip: {
          high: "High priority",
          medium: "Medium priority",
          low: "Low priority",
        },
        statusChip: {
          proposed: "Proposed",
          approved: "Approved",
          dismissed: "Dismissed",
        },
      },
      sliceBehavior: {
        sectionTitle: "Slice behavior",
        sectionHint:
          "Configure how pre-aggregated warehouse rows (totals) are filtered for overall metrics vs breakdowns.",
        aggregateValuesHint: "Comma-separated codes that represent rolled-up totals",
        defaultValueHint: "Code applied when the user does not filter on this slice",
        defaultFilterHint: "Optional SQL override instead of default value equality",
        fields: {
          aggregateValues: "Aggregate values",
          defaultValue: "Default value",
          defaultFilter: "Default filter",
          excludeOnBreakdown: "Exclude aggregate values on breakdown",
        },
      },
    },
    dataQuality: {
      tab: "Data Quality",
      integrity: {
        title: "Integrity check",
        description:
          "Checks that relationships and rules still point at entities and fields present in the current model.",
        runAction: "Run check",
        notRunYet: "Not run yet.",
        ok: "No integrity issues found.",
      },
      reconciliation: {
        title: "Reconciliation checks",
        description:
          "Compares a semantic-model-derived value against an independently-computed value.",
        newAction: "New check",
        runAction: "Run",
        empty: "No reconciliation checks configured yet.",
        neverRun: "Never run yet.",
        toleranceChip: "±{{tolerance}}%",
        resultSummary: "Primary: {{primary}} · Independent: {{independent}}",
      },
      newCheckModal: {
        title: "New reconciliation check",
        closeAria: "Close dialog",
        fields: {
          name: "Name",
          metricKey: "Metric key",
          primarySql: "Primary SQL",
          independentSql: "Independent-source SQL",
          tolerance: "Tolerance",
          toleranceHint: "Fraction of the independent value, e.g. 0.05 = within 5%",
        },
        validation: {
          required: "This field is required.",
          invalidTolerance: "Enter a tolerance of 0 or greater.",
        },
        actions: {
          cancel: "Cancel",
          create: "Create check",
        },
        errors: {
          saveFailed: "Could not create the reconciliation check. Please try again.",
        },
      },
    },
  },
  connections: {
    loadFailed: "Could not load connections.",
    menuAria: "Actions for {{name}}",
  },
  connectionDetail: {
    backAria: "Back to connections",
    notFound: "Connection not found",
    loading: "Loading connection",
    tabs: {
      aria: "Connection detail sections",
      schemaBrowser: "Schema Browser",
      semanticReadiness: "Semantic Readiness",
    },
    schemaBrowser: {
      tablesTitle: "Tables ({{count}})",
      searchPlaceholder: "Search tables...",
      searchAria: "Search tables",
      loading: "Loading tables",
      loadError: "Could not load tables",
      emptyTables: "No tables found",
      rowCountPlaceholder: "— rows",
      rowsLabel: "{{count}} rows",
      unsupportedConnectionType:
        "Schema browser is only available for Snowflake and Cube connections",
      emptyTitle: "Select a table to view schema",
      emptySubtitle:
        "Choose a table from the sidebar to see its columns and sample data",
      columnsTitle: "Columns ({{count}})",
      sampleDataTitle: "Sample Data (First 3 rows)",
      columnName: "Column Name",
      columnType: "Type",
      nullable: "Nullable",
      primaryKey: "Primary Key",
      foreignKey: "Foreign Key",
      nullableYes: "Yes",
      nullableNo: "No",
      pkFkPlaceholder: "-",
      emptyCell: "-",
      sampleLoading: "Loading sample data",
      sampleLoadError: "Could not load sample data",
    },
    semanticReadiness: {
      placeholder: "Semantic readiness content coming soon",
    },
  },
  connectionType: {
    snowflake: "Snowflake",
    cube: "Cube",
    mysql: "MySQL",
  },
  newConnectionModal: {
    title: "New Connection",
    closeAria: "Close dialog",
    tabs: {
      aria: "Connection type",
    },
    fields: {
      name: "Connection Name",
      account: "Account Identifier",
      warehouse: "Warehouse",
      database: "Database",
      user: "User",
      host: "Host",
      port: "Port",
      authMethod: "Auth Method",
      authType: "Auth Type",
      privateKey: "Private Key",
      privateKeyPassphrase: "Private Key Passphrase",
      password: "Password",
      tokenUrl: "Token URL",
      clientId: "Client ID",
      clientSecret: "Client Secret",
      baseUrl: "Base URL",
    },
    authMethod: {
      privateKey: "Private Key",
      password: "Password",
    },
    authType: {
      oauth2ClientCredentials: "OAuth2 Client Credentials",
    },
    actions: {
      testConnection: "Test Connection",
      testingConnection: "Testing...",
      cancel: "Cancel",
      saveConnection: "Save Connection",
    },
    validation: {
      required: "This field is required.",
      invalid: "Enter a valid value.",
    },
    errors: {
      saveFailed: "Could not save the connection. Please try again.",
      testFailed: "Could not test the connection. Please try again.",
    },
    testSuccess: "Connection successful.",
  },
  newDomainModal: {
    title: "New Domain",
    closeAria: "Close dialog",
    mode: {
      create: "Create new",
      import: "Import from file",
    },
    import: {
      chooseFile: "Choose export file",
      required: "Choose an exported domain file to import.",
      invalidFile: "This file isn't a valid domain export.",
    },
    fields: {
      name: "Name",
      description: "Description",
      connection: "Connection",
    },
    actions: {
      cancel: "Cancel",
      addDomain: "Add Domain",
      importDomain: "Import Domain",
    },
    validation: {
      required: "This field is required.",
    },
    errors: {
      saveFailed: "Could not save the domain. Please try again.",
    },
  },
} as const;
